/* istanbul ignore file */

import { useCallback } from 'react';
import {
  type DressingWeight,
  ModifierGroupVariation,
} from '@sg/graphql-schema';

import { type CustomizationMachineTypes } from '../../machine';
import { type CustomizationMachineActorRef } from '../../machine/customization.machine';
import { useCustomizationContext } from '../useCustomizationContext';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Customization helpers to interact with the customization machine.
 */
export const useCustomizationHelpers = (params: Params) => {
  const { actor, onAddIngredient, onRemoveIngredient } = params;

  const { send } = actor;

  // ─── Context ─────────────────────────────────────────────────────────

  const {
    allModificationsMap,
    activeModificationsCountByGroups,
    maxModificationsByGroups,
  } = useCustomizationContext({ actor });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const startCustomization = useCallback(() => {
    send({ type: 'customization.start' });
  }, [send]);

  const continueCustomization = useCallback(() => {
    send({ type: 'customization.continue' });
  }, [send]);

  const endCustomization = useCallback(() => {
    send({ type: 'customization.complete' });
  }, [send]);

  const completeCustomization = useCallback(() => {
    send({ type: 'customization.complete' });
  }, [send]);

  const selectDetails = useCallback(() => {
    send({ type: 'customization.complete' });
  }, [send]);

  const addIngredient = useCallback(
    (ingredientId: string) => {
      send({ type: 'customization.add-modification', ingredientId });
      onAddIngredient?.(ingredientId);
    },
    [send, onAddIngredient],
  );

  const removeIngredient = useCallback(
    (ingredientId: string) => {
      send({ type: 'customization.remove-modification', ingredientId });
      onRemoveIngredient?.(ingredientId);
    },
    [send, onRemoveIngredient],
  );

  const removeIngredientCompletely = useCallback(
    (ingredientId: string) => {
      const modification = allModificationsMap?.get(ingredientId);
      const isSplitPortion = modification?.modificationGroupVariation === ModifierGroupVariation.SplitPortion; // prettier-ignore

      if (isSplitPortion) {
        send({ type: 'customization.remove-split-portion-completely', ingredientId }); // prettier-ignore
        onRemoveIngredient?.(ingredientId);

        return;
      }

      send({ type: 'customization.remove-modification-completely', ingredientId }); // prettier-ignore
      onRemoveIngredient?.(ingredientId);
    },
    [allModificationsMap, send, onRemoveIngredient],
  );

  const setDressingModeToOnTheSide = useCallback(() => {
    actor.send({ type: 'customization.dressing-mode.on-the-side' });
  }, [actor]);

  const setDressingModeToMixedIn = useCallback(() => {
    actor.send({ type: 'customization.dressing-mode.mixed-in' });
  }, [actor]);

  const changeDressingPortions = useCallback(
    (ingredientId: string, portions: number) => {
      send({
        type: 'customization.dressing.change-portions',
        ingredientId,
        portions,
      });
    },
    [send],
  );

  const changeDressingWeight = useCallback(
    (ingredientId: string, weight: DressingWeight) => {
      send({
        type: 'customization.dressing.change-weight',
        ingredientId,
        weight,
      });
    },
    [send],
  );

  const toggleSplitPortion = useCallback(
    (ingredientId: string) => {
      send({ type: 'customization.toggle-split-portion', ingredientId });
    },
    [send],
  );

  // ─── Derived Helpers ─────────────────────────────────────────────────

  const getGroupCounts = useCallback(
    (groupName: string) => {
      const activeModificationsCount = activeModificationsCountByGroups[groupName] ?? 0; // prettier-ignore
      const maxModificationsCountForGroup = maxModificationsByGroups[groupName];

      const hasValidMaxCount = checkIfValidCount(maxModificationsCountForGroup);

      if (!hasValidMaxCount) return;

      return {
        activeModificationsCount,
        maxModificationsCountForGroup,
      };
    },
    [activeModificationsCountByGroups, maxModificationsByGroups],
  );

  const getFormattedQuantity = useCallback(
    (groupName: string) => {
      const counts = getGroupCounts(groupName);

      if (!counts) return;

      const { activeModificationsCount, maxModificationsCountForGroup } =
        counts;

      return `${activeModificationsCount}/${maxModificationsCountForGroup}`;
    },
    [getGroupCounts],
  );

  const checkIfCanAddMoreModifications = useCallback(
    (groupName: string) => {
      const counts = getGroupCounts(groupName);

      if (!counts) return false;

      const { activeModificationsCount, maxModificationsCountForGroup } =
        counts;

      return activeModificationsCount < maxModificationsCountForGroup;
    },
    [getGroupCounts],
  );

  const getMaxPortionsForGroup = useCallback(
    (groupName: string) => {
      const counts = getGroupCounts(groupName);

      return counts?.maxModificationsCountForGroup ?? Number.POSITIVE_INFINITY;
    },
    [getGroupCounts],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    startCustomization,
    endCustomization,
    continueCustomization,
    completeCustomization,
    selectDetails,
    addIngredient,
    removeIngredient,
    removeIngredientCompletely,
    setDressingModeToOnTheSide,
    setDressingModeToMixedIn,
    getMaxPortionsForGroup,
    getFormattedQuantity,
    checkIfCanAddMoreModifications,
    changeDressingPortions,
    changeDressingWeight,
    toggleSplitPortion,
    sortModifications,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfValidCount(count: unknown) {
  return typeof count === 'number' && !Number.isNaN(count);
}

function sortModifications(
  initialModificationsMap: CustomizationMachineTypes.ModificationsMap,
  modificationGroup: CustomizationMachineTypes.ProductModifierGroup,
) {
  const unsorted = modificationGroup.modifications;

  return [...unsorted].sort((modification, nextModification) => {
    const { id } = modification;
    const { id: nextId } = nextModification;

    const isModificationInitial = initialModificationsMap.has(id);
    const isNextModificationInitial = initialModificationsMap.has(nextId);

    if (isModificationInitial && !isNextModificationInitial) return -1;
    if (!isModificationInitial && isNextModificationInitial) return 1;

    return 0;
  });
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  actor: CustomizationMachineActorRef;
  onAddIngredient?: (ingredientId: string) => void;
  onRemoveIngredient?: (ingredientId: string) => void;
  onRemoveIngredientCompletely?: (ingredientId: string) => void;
};
